<template>
   <div v-if="loaded">
      <div v-if="section === 'upload'">
         <b-card v-if="projectId && projectUploadCategories.length > 0" class="mb-3">
            <h3>Documents</h3>
            <p id="manager-message-description">
               Do you have any supporting documents to add?
               {{ uploadsCopy || '' }}
            </p>

            <UploadTable :projectId="projectId" />
         </b-card>

         <div class="d-flex justify-content-between">
            <b-button
               id="btn-questionnaire"
               variant="secondary"
               style="font-size: 0.75rem"
               :to="questionnaireRoute"
            >
               <b-icon-arrow-left-short /> Back to Questionnaire
            </b-button>
            <b-button
               id="btn-review"
               variant="primary"
               style="font-size: 0.75rem"
               @click="section = 'review'"
            >
               Review my Answers
            </b-button>
         </div>
      </div>

      <div v-else>
         <ManagerMessage>
            <template #short>
               <p class="mb-0" v-if="isSectionCompleted" style="color: black !important">
                  This section is complete.
               </p>
               <p class="mb-0" v-else-if="readyToCompleteSection" style="color: black !important">
                  This section is ready to mark complete.
               </p>
               <p class="mb-0" v-else-if="!isCompleted" style="color: black !important">
                  Review these answers before completing this questionnaire.
               </p>
               <p class="mb-0" style="color: black !important" v-else>
                  This questionnaire has been completed.
               </p>
            </template>

            <template v-if="isSectionCompleted">
               <h2 class="card-title">This section is complete.</h2>

               <p class="card-description mb-0">
                  This section has been completed, so as long as everything still looks correct,
                  you're all done here! To make any changes to this section, you'll need to unlock
                  it first.
               </p>

               <div class="pt-3 d-flex align-items-center justify-content-end">
                  <b-button
                     v-if="!isSME"
                     id="btn-unlock-questionnaires"
                     class="dark-button mr-2"
                     variant="primary"
                     style="font-size: 0.75rem"
                     @click="unlockSection"
                  >
                     Unlock
                  </b-button>
                  <b-button
                     variant="success"
                     style="font-size: 0.75rem"
                     :to="{name: 'Home'}"
                     class="action-button ml-2"
                  >
                     Dashboard
                  </b-button>
               </div>
            </template>
            <template v-else-if="readyToCompleteSection">
               <h2 class="card-title">This section is ready to mark complete.</h2>

               <p class="card-description mb-0">
                  All of your questionnaires have been answered. If you're confident all the
                  information is accurate, click <b>Mark Section Complete</b> below.
               </p>
            </template>
            <template v-else-if="!isCompleted">
               <h2 style="color: black !important">
                  Review these answers before completing this questionnaire.
               </h2>

               <p class="mb-0" style="color: black !important">
                  Let's be sure everything looks correct here. When you're ready, click
                  <b>{{ isSME ? 'Done' : 'Complete' }}</b
                  >.
               </p>
            </template>
            <template v-else>
               <h2 style="color: black !important">This questionnaire has been completed.</h2>

               <p style="color: black !important">
                  Visit the dashboard to see if we still need more info from you.
               </p>

               <div class="d-flex align-items-center justify-content-end">
                  <b-button variant="success" style="font-size: 0.75rem" :to="{name: 'Home'}"
                     >Dashboard</b-button
                  >
               </div>
            </template>
         </ManagerMessage>

         <b-card class="mb-3" v-for="(question, index) in questions" :key="question.id">
            <p
               v-for="(paragraph, pIndex) in toParagraphs(question.question)"
               :id="pIndex === 0 ? `question-${index + 1}` : undefined"
               :key="pIndex"
               class="h5"
            >
               <span :style="pIndex !== 0 ? 'visibility: hidden' : ''"> {{ index + 1 }}. </span>
               {{ paragraph }}
            </p>
            <component
               :is="answerComponent(question)"
               :index="index"
               :readonly="true"
               class="mt-4"
            />
         </b-card>

         <div class="d-flex justify-content-between">
            <b-button
               v-if="projectUploadCategories.length > 0"
               id="btn-upload"
               variant="secondary"
               @click="section = 'upload'"
               style="font-size: 0.75rem"
            >
               <b-icon-arrow-left-short /> Upload Documents
            </b-button>
            <b-button
               v-else
               id="btn-questionnaire"
               class="dark-button"
               variant="primary"
               :to="questionnaireRoute"
               style="font-size: 0.75rem"
            >
               <b-icon-arrow-left-short /> Back to Questionnaire
            </b-button>

            <b-button
               v-if="!isSectionCompleted && !isCompleted && isSME"
               id="btn-complete"
               variant="success"
               v-b-modal.modal-complete-questionnaire
               style="font-size: 0.75rem"
            >
               Done <b-icon-check />
            </b-button>
            <b-button
               v-if="!isSectionCompleted && !isCompleted && !isSME"
               id="btn-complete"
               variant="success"
               v-b-modal.modal-complete-questionnaire
               style="font-size: 0.75rem"
            >
               Complete <b-icon-check />
            </b-button>
            <b-button
               v-if="readyToCompleteSection && !isSME"
               id="btn-complete-questionnaires"
               class="action-button"
               variant="success"
               style="font-size: 0.75rem"
               @click="completeSection"
            >
               Mark Section Complete <b-icon-check />
            </b-button>
         </div>
      </div>

      <b-modal
         id="modal-complete-questionnaire"
         :title="isSME ? 'Done Answering?' : 'Complete Questionnaire?'"
         centered
         ok-title="OK"
         @ok="completeQuestionnaire"
      >
         <p v-if="isSME">
            Marking this questionaire as Done will unassign the questionnaire from you.
         </p>
         <p v-else>Are you sure you want to mark this questionnaire complete?</p>
      </b-modal>
   </div>
</template>

<script>
import {mapGetters} from 'vuex';

import {answerTypeComponent} from '../utils/answerTypes';
import UploadTable from '@/views/uploads/widgets/UploadTable';
import QuestionnairesTitle from '../../../components/QuestionnairesTitle.vue';

export default {
   components: {
      QuestionnairesTitle,
      UploadTable,
   },

   props: {
      isProject: {
         type: Boolean,
         default: false,
      },
   },

   data() {
      return {
         loaded: false,
         section: 'review',
      };
   },

   computed: {
      ...mapGetters({
         company: 'companies/currentCompany',
         industries: 'industries/industryMap',
         questions: 'questionnaire/questions',
         title: 'questionnaire/title',
         projectId: 'questionnaire/projectId',
         isCompleted: 'questionnaire/isCompleted',
         isSectionCompleted: 'questionnaire/isSectionCompleted',
         readyToCompleteSection: 'questionnaire/readyToCompleteSection',
         isClient: 'isClient',
         isSME: 'isSME',
         uploadCategories: 'uploads/uploadCategories',
         project: 'questionnaire/project',
      }),

      companyId() {
         return this.$route.params.id;
      },

      year() {
         return this.$route.params.year;
      },

      projectUploadCategories() {
         if (this.projectId === null) {
            return [];
         }
         return this.uploadCategories.filter((category) => {
            return category.projects.some((project) => project.id === this.projectId);
         });
      },

      questionnaireRoute() {
         const params = this.$route.params;
         const name = this.$route.name.replace('review', 'complete');
         return {name, params};
      },

      uploadsCopy() {
         return this.company.prompts.uploads;
      },
   },

   methods: {
      // The answer component used for displaying an answer
      answerComponent(answer) {
         return answerTypeComponent(answer.ansType);
      },

      toParagraphs(text) {
         return text ? text.split('\n') : text;
      },

      async completeQuestionnaire() {
         await this.blockingRequest('questionnaire/completeQuestionnaire', {
            companyId: this.companyId,
         });
         if (this.isSME) {
            this.loaded = false;
            this.$router.push({name: 'Home'});
         }
      },

      async unlockSection() {
         const msg =
            "Unlocking this section may delay your credit calculation. You will have to mark it complete again once you're done.";
         const title = 'Unlock this section?';
         const proceed = await this.$bvModal.msgBoxConfirm(msg, {
            title,
            centered: true,
         });
         if (!proceed) return;

         await this.blockingRequest('questionnaire/unlockSection', {
            companyId: this.companyId,
         });
      },

      /** Lock the Questionnaires section */
      async completeSection() {
         await this.blockingRequest('questionnaire/completeSection', {
            companyId: this.companyId,
         });
      },
   },

   async created() {
      const requests = [this.$store.dispatch('industries/loadIndustries')];
      await this.loadPage(requests);
   },

   async mounted() {
      if (this.isProject && this.projectUploadCategories.length > 0) {
         this.section = 'upload';
      }
      this.loaded = true;
   },
};
</script>

<style lang="scss" scoped>
.card-title {
   font-size: 1.25rem;
   font-weight: 700;
}

.card-description {
   font-size: 0.8125rem;
}
</style>
